import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { type ImmovableAssetFormType, getImmovableAssetFormSchema } from "Forms/Schemas/Assets/immovableAssetFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetsOverviewQueryKey,
  getListImmovableAssetItemsQueryKey,
  useCreateImmovableAssetItem,
  useDeleteAssetItem,
  useUpdateImmovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type ImmovableAssetItem, ImmovableAssetItemImmovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { IMMOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly immovableAsset?: ImmovableAssetItem;
  readonly open: boolean;
};

export const ImmovableAssetDrawer: FC<Props> = ({ onClose, immovableAsset, open }) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListImmovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateImmovableAssetItem();
  const { mutate: deleteItem } = useDeleteAssetItem();
  const { mutate: update } = useUpdateImmovableAssetItem();

  const immovableAssets: Option[] = (Object.keys(ImmovableAssetItemImmovableSubType) as Array<ImmovableAssetItemImmovableSubType>).map(
    (value) => ({
      value,
      name: t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType[IMMOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
    }),
  );

  const methods = useForm<ImmovableAssetFormType>({
    resolver: yupResolver(getImmovableAssetFormSchema(t)),
    mode: "onBlur",
  });

  const { control, handleSubmit, reset, watch, setValue } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        immovableAssetType: immovableAsset?.immovableSubType,
        name: immovableAsset?.name,
        value: immovableAsset?.amount.value,
        isCoveredByInsurance: immovableAsset?.isCoveredByInsurance,
        isUnderFinancing: immovableAsset?.isUnderFinancing,
        isUsableForGoals: immovableAsset?.isUsableForGoals,
        note: immovableAsset?.note ?? undefined,
      };
      reset(defaultValues);
      return;
    }

    reset({});
  }, [immovableAsset, open, reset]);

  const onSubmit = (data: ImmovableAssetFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        immovableSubType: data.immovableAssetType as ImmovableAssetItemImmovableSubType,
        isCoveredByInsurance: !!data.isCoveredByInsurance,
        isUnderFinancing: !!data.isUnderFinancing,
        isUsableForGoals: !!data.isUsableForGoals,
      };

      if (!immovableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: immovableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: immovableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Deleted));
            invalidate();
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const immovableAssetName = watch("name");

  const onChangeType = (value: string) => {
    if (immovableAssetName && immovableAssetName.length > 0) return;
    const preFilledName = immovableAssets.find((ia) => ia.value === value)?.name;
    setValue("name", preFilledName ?? "");
  };

  return (
    <FormProvider {...methods}>
      <Drawer
        open={open}
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        title={immovableAsset ? t(Resources.AnalysisDetail.Assets.Immovable.Edit) : t(Resources.AnalysisDetail.Assets.Immovable.Add)}
        onSubmit={handleSubmit(onSubmit)}
        startAction={
          !!immovableAsset && (
            <Button variant="text" color="error" disabled={readOnly} onClick={onDelete}>
              {t(Resources.Common.Delete)}
            </Button>
          )
        }
        primaryAction={
          <Button variant="contained" type="submit" disabled={readOnly}>
            {immovableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
          </Button>
        }
      >
        <Stack spacing={Spacing.XL} py={Spacing.XXL}>
          <FormSelect
            control={control}
            name="immovableAssetType"
            label={t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType.Title)}
            required
            options={toSelectOptions(immovableAssets)}
            placeholder={t(Resources.Common.SelectPlaceholder)}
            disabled={readOnly}
            onChange={onChangeType}
            fullWidth
          />
          <FormTextField
            control={control}
            name="name"
            label={t(Resources.AnalysisDetail.Assets.Name)}
            required
            disabled={readOnly}
            fullWidth
          />
          <FormNumberField
            control={control}
            name="value"
            label={t(Resources.AnalysisDetail.Assets.Value)}
            required
            scale={0}
            min={0}
            thousandsSeparator=" "
            unit={t(Resources.Common.CZKSuffix)}
            disabled={readOnly}
            placeholder="0"
            fullWidth
          />
          <FormTextField
            control={control}
            name="note"
            label={t(Resources.AnalysisDetail.Goals.GoalNote)}
            maxLength={NOTE_MAX_LENGTH}
            disabled={readOnly}
            multiline
            minRows={6}
            maxRows={8}
            fullWidth
          />
          <Divider />
          <Typography fontWeight={600} variant="subtitle1">
            {t(Resources.AnalysisDetail.Assets.Attributes)}
          </Typography>
          <Stack spacing={Spacing.S}>
            <FormCheckbox
              control={control}
              name="isUsableForGoals"
              label={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
              disabled={readOnly}
            />
            <FormCheckbox
              control={control}
              name="isCoveredByInsurance"
              label={t(Resources.AnalysisDetail.Assets.IsCoveredByInsurance)}
              disabled={readOnly}
            />
            <FormCheckbox
              control={control}
              name="isUnderFinancing"
              label={t(Resources.AnalysisDetail.Assets.IsUnderFinancing)}
              disabled={readOnly}
            />
          </Stack>
        </Stack>
      </Drawer>
    </FormProvider>
  );
};
